import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/api';
import {
  ButtonSpinner,
  FormField,
  inputClasses,
  primaryButtonClasses,
} from '@/components/auth/FormField';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    try {
      await api.post<{ ok: true }>('/api/auth/request-password-reset', { email });
    } catch {
      // Endpoint is non-enumerating on the server; treat network failures the
      // same way and show the generic confirmation.
    } finally {
      setSentTo(email);
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      <Link to="/login" className="mb-10 flex items-center gap-2 text-sm text-[color:var(--color-fg-muted)]">
        <span aria-hidden>←</span> Back to login
      </Link>
      <div className="flex flex-1 flex-col justify-center gap-8">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Reset password</h1>
          <p className="text-sm text-[color:var(--color-fg-muted)]">
            Enter your email and we'll send you a link to choose a new password.
          </p>
        </header>

        {sentTo ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm">
              If an account with <b>{sentTo}</b> exists, we sent a reset link.
              Check your inbox — the link expires in 1 hour.
            </p>
            <button
              type="button"
              onClick={() => {
                setSentTo(null);
              }}
              className="self-start text-sm text-[color:var(--color-fg)] underline underline-offset-2"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <FormField label="Email" htmlFor="email">
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                disabled={pending}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClasses}
              />
            </FormField>
            <button
              type="submit"
              disabled={pending}
              aria-busy={pending}
              className={primaryButtonClasses}
            >
              {pending ? (
                <>
                  <ButtonSpinner />
                  <span>Sending…</span>
                </>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
